import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UseMutateFunction } from "@tanstack/react-query";
import { useFetchContractUpdateService } from "./services/use-fetch-contract-update-service";
import { useContractUpdateActionService } from "./services/use-contract-update-action-service";
import ContractUpdateSkeleton from "./components/contract-update-skeleton";
import ContractUpdateLayout from "./components/contract-update-layout";
import ContractUpdateCard from "./components/contract-update-card";
import AcceptedScreen from "./components/accepted-screen";
import RefusedScreen from "./components/refused-screen";
import ErrorScreen from "./components/error-screen";
import { ApiError } from "./types";

const ContractUpdatePage: FC = () => {
  const { t, i18n } = useTranslation(["contract-updates"]);

  const [signedParams] = useState<Record<string, string>>(() =>
    Object.fromEntries(new URLSearchParams(window.location.search)),
  );

  const { data, isLoading, error } = useFetchContractUpdateService(signedParams);

  const [view, setView] = useState<"accepted" | "refused" | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const contractUpdate = data?.meta?.contract_update;

  const { mutate: accept, isPending: accepting } = useContractUpdateActionService(contractUpdate?.accept_url);
  const { mutate: refuse, isPending: refusing } = useContractUpdateActionService(contractUpdate?.decline_url);

  useEffect(() => {
    const lang = signedParams.lang || contractUpdate?.lang;
    if (lang) i18n.changeLanguage(lang);
  }, [contractUpdate?.lang, i18n, signedParams.lang]);

  const runAction = (
    mutate: UseMutateFunction<void, Error, void>,
    nextView: "accepted" | "refused",
    errorKey: string,
    fallback: string,
  ) => {
    setActionError(null);
    mutate(undefined, {
      onSuccess: () => setView(nextView),
      onError: (err) =>
        setActionError((err as ApiError).response?.data?.message ?? t(errorKey, fallback)),
    });
  };

  const currentStatus = view ?? contractUpdate?.status;

  return (
    <ContractUpdateLayout>
      {isLoading ? (
        <ContractUpdateSkeleton />
      ) : error || !contractUpdate ? (
        <ErrorScreen error={error} />
      ) : currentStatus === "accepted" ? (
        <AcceptedScreen contractId={contractUpdate.contract_id} />
      ) : currentStatus === "refused" ? (
        <RefusedScreen
          contractId={contractUpdate.contract_id}
          companyName={contractUpdate.company_name}
          companyEmail={contractUpdate.company_email}
        />
      ) : (
        <ContractUpdateCard
          contractUpdate={contractUpdate}
          accepting={accepting}
          refusing={refusing}
          onAccept={() =>
            runAction(accept, "accepted", "contract-updates:error-accept-failed", "Unable to accept the changes. Please try again.")
          }
          onRefuse={() =>
            runAction(refuse, "refused", "contract-updates:error-refuse-failed", "Unable to refuse the changes. Please try again.")
          }
          actionError={actionError}
        />
      )}
    </ContractUpdateLayout>
  );
};

export default ContractUpdatePage;
